// Canadian tax service
export const PROVINCES = [
  { code: 'QC', name: 'Québec' },
  { code: 'ON', name: 'Ontario' },
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'Colombie-Britannique' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'Nouveau-Brunswick' },
  { code: 'NL', name: 'Terre-Neuve-et-Labrador' },
  { code: 'NS', name: 'Nouvelle-Écosse' },
  { code: 'PE', name: 'Île-du-Prince-Édouard' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NT', name: 'Territoires du Nord-Ouest' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' },
];

// Rates current as of 2026. Sources: CRA (GST/HST) and each province's
// revenue agency (PST/RST). Nova Scotia's HST dropped from 15% to 14%
// effective April 1, 2025.
const PROVINCE_TAX = {
  QC: { type: 'gst-qst', gst: 0.05, qst: 0.09975 },
  ON: { type: 'hst', hst: 0.13 },
  NB: { type: 'hst', hst: 0.15 },
  NL: { type: 'hst', hst: 0.15 },
  PE: { type: 'hst', hst: 0.15 },
  NS: { type: 'hst', hst: 0.14 },
  BC: { type: 'gst-pst', gst: 0.05, pst: 0.07, pstLabel: 'PST' },
  SK: { type: 'gst-pst', gst: 0.05, pst: 0.06, pstLabel: 'PST' },
  MB: { type: 'gst-pst', gst: 0.05, pst: 0.07, pstLabel: 'RST' },
  AB: { type: 'gst', gst: 0.05 },
  NT: { type: 'gst', gst: 0.05 },
  NU: { type: 'gst', gst: 0.05 },
  YT: { type: 'gst', gst: 0.05 },
};

function pct(rate) {
  const n = rate * 100;
  return Number.isInteger(n) ? String(n) : String(+n.toFixed(3));
}

function line(amount, rate, labelFr, labelEn, lang) {
  const value = +(amount * rate).toFixed(2);
  const label = lang === 'en'
    ? `${labelEn} (${pct(rate)}%)`
    : `${labelFr} (${pct(rate)}%)`;
  return { label, amount: value };
}

export function getTaxes(province, subtotal, lang = 'fr') {
  const amount = Number(subtotal) || 0;
  const p = (province || 'QC').toUpperCase();
  const cfg = PROVINCE_TAX[p] || PROVINCE_TAX.QC;

  let breakdown;
  if (cfg.type === 'gst-qst') {
    breakdown = [
      line(amount, cfg.gst, 'TPS', 'GST', lang),
      line(amount, cfg.qst, 'TVQ', 'QST', lang),
    ];
  } else if (cfg.type === 'hst') {
    breakdown = [line(amount, cfg.hst, 'TVH', 'HST', lang)];
  } else if (cfg.type === 'gst-pst') {
    breakdown = [
      line(amount, cfg.gst, 'TPS', 'GST', lang),
      line(amount, cfg.pst, cfg.pstLabel === 'RST' ? 'RST' : 'TVP', cfg.pstLabel, lang),
    ];
  } else {
    breakdown = [line(amount, cfg.gst, 'TPS', 'GST', lang)];
  }

  const total = +breakdown.reduce((s, b) => s + b.amount, 0).toFixed(2);
  return { breakdown, total };
}

export function computeSummary(materials = [], labour = {}, margin = 15, miscFees = 0, province = 'QC', miscNotes = '', lang = 'fr') {
  const materialsTotal = materials.reduce(
    (s, m) => s + (Number(m.quantity) || 0) * (Number(m.unit_price) || 0),
    0
  );

  let labourTotal = 0;
  const uniform = labour.uniform !== false; // default true (backward compatible)
  if (uniform) {
    labourTotal =
      (Number(labour.hourly_rate) || 0) *
      (Number(labour.workers) || 0) *
      (Number(labour.hours) || 0);
  } else {
    const list = Array.isArray(labour.individuals) ? labour.individuals : [];
    labourTotal = list.reduce(
      (s, w) => s + (Number(w.rate) || 0) * (Number(w.hours) || 0),
      0
    );
  }

  const baseCost = materialsTotal + labourTotal;
  const marginPct = Number(margin) || 0;
  const marginAmount = +(baseCost * (marginPct / 100)).toFixed(2);
  const misc = Number(miscFees) || 0;
  const subtotal = +(baseCost + marginAmount + misc).toFixed(2);
  const taxes = getTaxes(province, subtotal, lang);
  const grandTotal = +(subtotal + taxes.total).toFixed(2);

  // ===== "Display" amounts — margin folded in so client never sees it =====
  // Pro-rate the margin across labour & materials so the math adds up on the
  // PDF and signature page. If only one of the two is present, fold it there.
  let displayMaterials = materialsTotal;
  let displayLabour = labourTotal;
  if (baseCost > 0 && marginAmount > 0) {
    if (labourTotal > 0 && materialsTotal > 0) {
      const labourShare = +(marginAmount * (labourTotal / baseCost)).toFixed(2);
      displayLabour = +(labourTotal + labourShare).toFixed(2);
      displayMaterials = +(materialsTotal + (marginAmount - labourShare)).toFixed(2);
    } else if (labourTotal > 0) {
      displayLabour = +(labourTotal + marginAmount).toFixed(2);
    } else {
      displayMaterials = +(materialsTotal + marginAmount).toFixed(2);
    }
  }

  return {
    materialsTotal: +materialsTotal.toFixed(2),
    labourTotal: +labourTotal.toFixed(2),
    // Display variants — used by PDF, SignPage and anywhere client-facing.
    // These hide the margin by folding it into labour/materials proportionally.
    displayMaterials,
    displayLabour,
    baseCost: +baseCost.toFixed(2),
    marginPct,
    marginAmount,
    miscFees: +misc.toFixed(2),
    miscNotes: (miscNotes || '').toString().trim(),
    subtotal,
    taxes: taxes.breakdown,
    taxesTotal: taxes.total,
    grandTotal,
  };
}
